/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Locale, LocaleKeys } from "../locale";

/**
 * One reading of "what went wrong", shared by every surface that puts a failed
 * service call in front of the user.
 *
 * Three shapes reach a catch block, and each hides its text somewhere else:
 *
 *  - A **mapped service error** (`ClaimServiceError`, `DomainTokenServiceError`,
 *    `MsoDefaultDeviceConfigError`, …). `AppError` does **not** extend `Error`,
 *    so `error instanceof Error` is false and `String(error)` is
 *    "[object Object]". Its text is behind `getDescription()` / `getMessage()`.
 *  - A **raw axios error**, from an endpoint whose interactor rethrows rather
 *    than wrapping (`v1/idpJwtToken`). Axios overwrites `message` with its own
 *    summary — "Request failed with status code 401" — and leaves what the
 *    service actually said in the `{ code, message }` body on
 *    `error.response.data`.
 *  - A plain `Error` thrown by our own validation, whose `message` is the text.
 *
 * Reading only one of the three is why a rejected credential showed "Request
 * failed with status code 401" instead of "invalid username or password", and
 * why a mapped error could reach the alert as "[object Object]" or as a bare
 * generic fallback.
 */

/**
 * The `{ code, message }` error envelope carried in the response body,
 * unwrapped one level when the service uses the nested `{ error: … }` form.
 *
 * `undefined` when the error carries no response body: a transport failure, or
 * an already-wrapped `AppError`, which keeps no response.
 */
function readErrorEnvelope(
  error: unknown
): Record<string, unknown> | undefined {
  if (error === null || error === undefined || typeof error !== "object") {
    return undefined;
  }
  const response: unknown = (error as { response?: unknown }).response;
  if (
    response === null ||
    response === undefined ||
    typeof response !== "object"
  ) {
    return undefined;
  }
  const data: unknown = (response as { data?: unknown }).data;
  if (data === null || data === undefined || typeof data !== "object") {
    return undefined;
  }
  const nested: unknown = (data as { error?: unknown }).error;
  if (nested !== null && nested !== undefined && typeof nested === "object") {
    return nested as Record<string, unknown>;
  }
  return data as Record<string, unknown>;
}

/** A non-empty string, or `undefined` for anything else. */
function asText(value: unknown): string | undefined {
  if (typeof value === "string" && value.length > 0) {
    return value;
  }
  return undefined;
}

/**
 * The service's own error code (`IAS.UNAUTHORIZED`, `unauthorized`, …) from the
 * response body, or `undefined` when there is none.
 *
 * Worth preferring over `error.code` wherever the envelope's message is shown:
 * the transport code beside it is only ever the generic `ERR_BAD_REQUEST`,
 * which names no cause a support agent can look up.
 */
export function extractServiceErrorCode(error: unknown): string | undefined {
  const envelope: Record<string, unknown> | undefined =
    readErrorEnvelope(error);
  if (envelope === undefined) {
    return undefined;
  }
  return asText(envelope.code);
}

/**
 * The service's own explanation of the failure from the response body, or
 * `undefined` when the response carried none.
 */
export function extractServiceErrorBodyMessage(
  error: unknown
): string | undefined {
  const envelope: Record<string, unknown> | undefined =
    readErrorEnvelope(error);
  if (envelope === undefined) {
    return undefined;
  }
  return asText(envelope.message);
}

/**
 * The localised string for `key`, guaranteed non-empty — the value to hand to
 * {@link extractServiceErrorMessage} as its `fallbackMessage`.
 *
 * Exists because the two halves of that contract pull against each other.
 * `Locale.getString` answers `""` for a key it does not have, and the fallback
 * is by definition the last thing standing between the user and a blank error
 * body, so passing a raw `getString` straight through would reintroduce the
 * silent-empty failure `localeKeyCoverage.test.ts` was written for. Falling
 * back to the generic "Something went wrong. Please try again." — present in
 * every bundle, en/es/fr — keeps the message non-empty, and stops applying by
 * itself the moment the specific string lands.
 *
 * Deliberately a function, not a module-level constant: `Locale.setLocale` is
 * async and fills `languageMap` after import, so a `const` captured at module
 * scope would freeze `""` forever and never see a locale change.
 */
export function localizedErrorFallback(key: string): string {
  const message: string = Locale.getString(key);
  if (message.length > 0) {
    return message;
  }
  return Locale.getString(LocaleKeys.COMMON_ERROR_UNKNOWN);
}

/**
 * Best available human-readable text for anything a service call threw.
 *
 * Order, most specific first: a mapped error's description — the interactors
 * call `setDescription()` with the localised string — then its message, then
 * the response body's envelope message, then a plain `Error.message`, then the
 * caller's fallback.
 *
 * The envelope has to outrank `Error.message` because axios replaces the body
 * text with its own status summary; it sits *below* the mapped fields because
 * those are the localised copy, read from the same body by the interactor.
 *
 * `fallbackMessage` should already be localised — callers render the result
 * verbatim.
 */
export function extractServiceErrorMessage(
  error: unknown,
  fallbackMessage: string
): string {
  if (error !== null && error !== undefined && typeof error === "object") {
    const candidate = error as {
      getDescription?: () => string;
      getMessage?: () => string;
      description?: unknown;
    };
    if (typeof candidate.getDescription === "function") {
      const described: string | undefined = asText(candidate.getDescription());
      if (described !== undefined) {
        return described;
      }
    }
    // `AppError` keeps `description` as a plain property behind its accessor,
    // and the response-integrity errors are sometimes read as bare records, so
    // the field is honoured whether or not the accessor came with it.
    const description: string | undefined = asText(candidate.description);
    if (description !== undefined) {
      return description;
    }
    if (typeof candidate.getMessage === "function") {
      const message: string | undefined = asText(candidate.getMessage());
      if (message !== undefined) {
        return message;
      }
    }
  }
  const bodyMessage: string | undefined = extractServiceErrorBodyMessage(error);
  if (bodyMessage !== undefined) {
    return bodyMessage;
  }
  if (error instanceof Error) {
    const message: string | undefined = asText(error.message);
    if (message !== undefined) {
      return message;
    }
  }
  return fallbackMessage;
}
